import React from "react";
import { Button, Select, Switch, message } from "antd";
import {
  ISO_WEEKDAY_OPTIONS,
  getIanaTimezoneOptions,
  isAdvancedAccountAvailable
} from "../utils/advancedCampaignForm";

const WEEKDAY_ACCESSIBLE_LABELS = {
  1: "Monday",
  2: "Tuesday",
  3: "Wednesday",
  4: "Thursday",
  5: "Friday",
  6: "Saturday",
  7: "Sunday"
};

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

export default function AdvancedCampaignSettings({
  gmailAccounts,
  selectedGmailAccounts,
  onSelectedGmailAccountsChange,
  onAddGmailAccount,
  sendingWeekdays,
  onSendingWeekdaysChange,
  sendingFullDay,
  onSendingFullDayChange,
  sendingStartTime,
  onSendingStartTimeChange,
  sendingEndTime,
  onSendingEndTimeChange,
  campaignTimezone,
  onCampaignTimezoneChange
}) {
  const selectedLookup = new Set(selectedGmailAccounts.map(normalizeEmail));

  const accountByEmail = new Map(
    gmailAccounts.map((account) => [normalizeEmail(account.email), account])
  );

  const handleSelectAccount = (email) => {
    if (selectedGmailAccounts.length >= 10) {
      message.warning("A campaign can use at most 10 Gmail accounts.");
      return;
    }
    if (selectedLookup.has(normalizeEmail(email))) {
      return;
    }
    onSelectedGmailAccountsChange([...selectedGmailAccounts, email]);
  };

  const handleRemoveAccount = (index) => {
    onSelectedGmailAccountsChange(
      selectedGmailAccounts.filter((_, selectedIndex) => selectedIndex !== index)
    );
  };

  const toggleWeekday = (weekday) => {
    const selected = sendingWeekdays.includes(weekday);
    const nextWeekdays = selected
      ? sendingWeekdays.filter((value) => value !== weekday)
      : [...sendingWeekdays, weekday].sort((a, b) => a - b);
    onSendingWeekdaysChange(nextWeekdays);
  };

  return (
    <div className="advanced-sending-panel">
      <section className="form-group advanced-accounts-section" aria-labelledby="gmail-accounts-heading">
        <div className="advanced-section-heading">
          <span id="gmail-accounts-heading" className="field-label">
            <span className="required-indicator" aria-hidden="true">*</span> Gmail Accounts
          </span>
          <strong className="advanced-selected-count" aria-live="polite">
            {selectedGmailAccounts.length} of 10 selected
          </strong>
        </div>

        {selectedGmailAccounts.length > 0 ? (
          <div
            className="advanced-selected-accounts"
            role="list"
            aria-label="Selected Gmail accounts in sending order"
          >
            {selectedGmailAccounts.map((email, index) => {
              const account = accountByEmail.get(normalizeEmail(email));
              const needsReconnect = !isAdvancedAccountAvailable(account);
              return (
                <div className="advanced-selected-account" role="listitem" key={`${normalizeEmail(email)}-${index}`}>
                  <span className="advanced-account-order" aria-label={`Sending order ${index + 1}`}>
                    {index + 1}
                  </span>
                  <span className="advanced-account-details">
                    <span className="advanced-account-address">{email}</span>
                    {needsReconnect && (
                      <span className="advanced-account-status">Reconnect required</span>
                    )}
                  </span>
                  <Button
                    className="advanced-account-remove"
                    type="text"
                    danger
                    htmlType="button"
                    onClick={() => handleRemoveAccount(index)}
                    aria-label={`Deselect ${email} from this campaign`}
                  >
                    Remove
                  </Button>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="advanced-empty-selection" role="status">
            Select at least one connected Gmail account.
          </p>
        )}
        {selectedGmailAccounts.length > 0 && (
          <p className="advanced-selection-note">
            Remove only deselects an account from this campaign; it does not disconnect Gmail.
          </p>
        )}

        <div className="advanced-account-actions">
          <div className="advanced-connected-account-picker">
            <label className="advanced-control-label" htmlFor="advanced-connected-account-select">
              Select connected Gmail accounts
            </label>
            <Select
              id="advanced-connected-account-select"
              value={undefined}
              onChange={handleSelectAccount}
              className="advanced-account-select"
              placeholder={
                gmailAccounts.length === 0
                  ? "No Gmail accounts available"
                  : selectedGmailAccounts.length >= 10
                    ? "10-account limit reached"
                    : "Choose a connected account"
              }
              disabled={gmailAccounts.length === 0 || selectedGmailAccounts.length >= 10}
              virtual={false}
              listHeight={250}
              showSearch
              aria-label="Select a connected Gmail account for this campaign"
              filterOption={(input, option) =>
                String(option?.searchLabel || option?.label || "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={gmailAccounts.map((account) => {
                const selected = selectedLookup.has(normalizeEmail(account.email));
                const available = isAdvancedAccountAvailable(account);
                return {
                  value: account.email,
                  label: available
                    ? `${account.email}${selected ? " (selected)" : ""}`
                    : `${account.email} (reconnect required)`,
                  searchLabel: account.email,
                  disabled: selected || !available
                };
              })}
            />
          </div>
          <Button
            className="advanced-add-account-button"
            type="default"
            htmlType="button"
            onClick={onAddGmailAccount}
          >
            + Add Gmail Account
          </Button>
        </div>

        <div className="advanced-daily-estimate" aria-live="polite">
          <strong>
            Estimated daily maximum: {selectedGmailAccounts.length * 40} emails
            {" "}({selectedGmailAccounts.length} × 40)
          </strong>
          <span>Quota resets at midnight UTC.</span>
          <span>
            The 11–13-minute interval is never shortened. A narrow window may reduce actual sends.
          </span>
        </div>
      </section>

      <section className="form-group" aria-labelledby="sending-weekdays-heading">
        <span id="sending-weekdays-heading" className="field-label">
          <span className="required-indicator" aria-hidden="true">*</span> Sending Weekdays
        </span>
        <div className="weekday-options" role="group" aria-labelledby="sending-weekdays-heading">
          {ISO_WEEKDAY_OPTIONS.map(({ label, value }) => {
            const selected = sendingWeekdays.includes(value);
            return (
              <button
                key={value}
                type="button"
                className={`weekday-option${selected ? " weekday-option-selected" : ""}`}
                aria-pressed={selected}
                aria-label={`${WEEKDAY_ACCESSIBLE_LABELS[value]}, ${selected ? "selected" : "not selected"}`}
                onClick={() => toggleWeekday(value)}
              >
                <span className="weekday-check" aria-hidden="true">✓</span>
                <span>{label}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="form-group">
        <div className="full-day-row">
          <span id="full-day-window-label" className="field-label">Full-day sending window</span>
          <Switch
            checked={sendingFullDay}
            onChange={onSendingFullDayChange}
            aria-labelledby="full-day-window-label"
          />
        </div>
        {!sendingFullDay && (
          <div className="time-window-row">
            <label>
              <span>Start (inclusive)</span>
              <input
                type="time"
                value={sendingStartTime}
                onChange={(event) => onSendingStartTimeChange(event.target.value)}
              />
            </label>
            <label>
              <span>End (exclusive)</span>
              <input
                type="time"
                value={sendingEndTime}
                onChange={(event) => onSendingEndTimeChange(event.target.value)}
              />
            </label>
          </div>
        )}
      </section>

      <section className="form-group">
        <label>
          <span className="field-label">
            <span className="required-indicator" aria-hidden="true">*</span> Campaign Timezone
          </span>
          <Select
            value={campaignTimezone}
            onChange={onCampaignTimezoneChange}
            style={{ width: "100%" }}
            showSearch
            virtual={false}
            listHeight={300}
            aria-label="Campaign timezone"
            options={getIanaTimezoneOptions().map((timezone) => ({
              value: timezone,
              label: timezone
            }))}
          />
        </label>
      </section>
    </div>
  );
}
